import React, { useState } from 'react';
import {
  Paper,
  Grid,
  Button,
  useTheme,
  alpha,
  Typography
} from '@mui/material';
import { Event as EventIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

interface SearchRoomsProps {
  onSearch: (params: { checkIn: Date | null; checkOut: Date | null; guests: number }) => void;
}

const SearchRooms: React.FC<SearchRoomsProps> = ({ onSearch }) => {
  const [checkIn, setCheckIn] = useState<dayjs.Dayjs | null>(dayjs());
  const [checkOut, setCheckOut] = useState<dayjs.Dayjs | null>(dayjs().add(1, 'day'));
  const [guests, _setGuests] = useState('1');
  const theme = useTheme();
  const handleCheckInChange = (date: dayjs.Dayjs | null) => {
    setCheckIn(date);
    if(date && checkOut){
      onSearch({
        checkIn: date.toDate(),
        checkOut: checkOut.toDate(),
        guests: parseInt(guests, 10),
      });
    }
    // If check-out date is before or equal to the new check-in date, update it
    if (date && checkOut && (checkOut.isBefore(date) || checkOut.isSame(date, 'day'))) {
      setCheckOut(date.add(1, 'day'));
      onSearch({
        checkIn: date.toDate(),
        checkOut: date.add(1, 'day').toDate(),
        guests: parseInt(guests, 10),
      });
    }
    // setTimeout(function(){
    //   handleSearch();
    // }, 200)
  };

  const handleCheckOutChange = (date: dayjs.Dayjs | null) => {
    if (date && checkIn && date.isBefore(checkIn)) {
      return; // Don't allow check-out before check-in
    }
    setCheckOut(date);
    if(date && checkIn){
      onSearch({
        checkIn: checkIn.toDate(),
        checkOut: date.toDate(),
        guests: parseInt(guests, 10),
      });
    }
  };

  const handleSearch = () => {
    if (!checkIn || !checkOut) return;

    onSearch({
      checkIn: checkIn.toDate(),
      checkOut: checkOut.toDate(),
      guests: parseInt(guests, 10),
    });
  };

  const isSearchDisabled = !checkIn || !checkOut || checkOut.isBefore(checkIn) || checkOut.isSame(checkIn, 'day');

  return (
    <Paper elevation={2} sx={{
      background: alpha(theme.palette.primary.main, 0.05),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
      p: 2,
      mb: 2
    }}>
      <Grid container sx={{justifyContent: "space-between"}}>
        <Grid item xs={12} display="flex" sx={{ mb: 1 }}>
          <EventIcon color="primary" />
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 500 }}>
            Select Date Range
          </Typography>
        </Grid>
        <Grid item xs={6} sm={4} sx={{ p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Check-in Date"
              value={checkIn}
              onChange={handleCheckInChange}
              minDate={dayjs()}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={6} sm={4} sx={{ p: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Check-out Date"
              value={checkOut}
              onChange={handleCheckOutChange}
              minDate={checkIn ? checkIn.add(1, 'day') : dayjs().add(1, 'day')}
              disabled={!checkIn}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={4} sx={{ p: 1 }}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ height: '56px' }}
            onClick={handleSearch}
            disabled={isSearchDisabled}
          >
            Search Rooms
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default SearchRooms;
